<template>
  <div class="tickets-section">
    <v-card class="section-card" elevation="0">

      <!-- Card Header -->
      <div class="card-head">
        <div class="head-gradient head-gradient--red" />
        <div class="head-inner">
          <div class="d-flex align-center justify-space-between mb-2">
            <div>
              <h2 class="form-title">{{ label }}</h2>
              <div class="subtitle">ติดตามและส่งเรื่องแจ้งปัญหา</div>
            </div>
            <v-btn
              v-if="view === 'list'"
              class="create-btn"
              @click="view = 'create'"
            >
              <v-icon left small>mdi-plus</v-icon>
              สร้าง Ticket ใหม่
            </v-btn>
            <v-btn v-else text @click="handleBack">
              <v-icon left>mdi-arrow-left</v-icon>
              กลับ
            </v-btn>
          </div>
        </div>
      </div>

      <v-divider />

      <div class="section-form">

        <!-- ── LIST VIEW ── -->
        <template v-if="view === 'list'">
          <div class="d-flex align-center mb-4">
            <v-select
              v-model="filterStatus"
              :items="statusItems"
              label="กรองสถานะ"
              outlined
              dense
              hide-details
              style="max-width: 200px"
              @change="fetchTickets"
            />
          </div>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate size="32" width="3" color="#d6230b" />
          </div>

          <div v-else-if="tickets.length === 0" class="empty-wrap">
            <v-icon size="48" color="#9ca3af" class="mb-3">mdi-ticket-outline</v-icon>
            <div class="empty-title">ยังไม่มีรายการ Ticket</div>
            <div class="empty-sub">สร้าง Ticket เพื่อแจ้งปัญหาหรือสอบถามข้อมูล</div>
            <v-btn class="mt-4 create-btn" @click="view = 'create'">
              <v-icon left small>mdi-plus</v-icon>
              สร้าง Ticket แรก
            </v-btn>
          </div>

          <div v-else class="ticket-list">
            <div
              v-for="t in tickets"
              :key="t.id"
              class="ticket-row d-flex align-center"
              @click="openDetail(t.id)"
            >
              <div class="ticket-icon-wrap mr-4">
                <v-icon :color="t.status === 'open' ? '#d6230b' : '#9ca3af'" size="28">
                  mdi-ticket-outline
                </v-icon>
              </div>
              <div class="grow">
                <div class="ticket-title font-weight-bold mb-1">{{ t.title }}</div>
                <div class="ticket-sub text-body-2 grey--text text--darken-1">
                  {{ truncate(t.description, 80) }}
                </div>
                <div class="text-caption grey--text mt-1">
                  <v-icon x-small class="mr-1">mdi-calendar-outline</v-icon>
                  {{ formatDate(t.createdAt) }}
                </div>
              </div>
              <div class="ml-4 d-flex flex-column align-end" style="gap: 6px;">
                <v-chip
                  small
                  :color="t.status === 'open' ? '#fee2e2' : '#F3F4F6'"
                  :text-color="t.status === 'open' ? '#d6230b' : '#6b7280'"
                  class="status-chip"
                >
                  <v-icon left x-small>{{ t.status === 'open' ? 'mdi-lock-open-outline' : 'mdi-lock-outline' }}</v-icon>
                  {{ t.status === 'open' ? 'เปิดอยู่' : 'ปิดแล้ว' }}
                </v-chip>
                <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
              </div>
            </div>
          </div>
        </template>

        <!-- ── CREATE VIEW ── -->
        <template v-else-if="view === 'create'">
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model.trim="form.title"
              label="หัวข้อ *"
              outlined
              dense
              :rules="[v => !!v || 'กรุณาระบุหัวข้อ']"
              class="mb-3"
            />
            <v-textarea
              v-model.trim="form.description"
              label="รายละเอียดปัญหา *"
              outlined
              dense
              rows="4"
              :rules="[v => !!v || 'กรุณาระบุรายละเอียด']"
              class="mb-3"
            />
            <v-textarea
              v-model.trim="form.message"
              label="ข้อความเพิ่มเติม (ถ้ามี)"
              outlined
              dense
              rows="3"
              class="mb-4"
            />
            <div class="mb-2 text-subtitle-2 font-weight-medium">แนบรูปภาพ (ถ้ามี)</div>
            <v-file-input
              multiple
              accept="image/*"
              show-size
              truncate-length="25"
              prepend-icon="mdi-image-multiple"
              label="เลือกรูปภาพ"
              outlined
              dense
              @change="onCreateFilesSelected"
            />
            <v-row v-if="createPreviews.length" class="mt-3 mb-4" dense>
              <v-col v-for="(src, idx) in createPreviews" :key="idx" cols="6" sm="4" md="3">
                <v-img :src="src" aspect-ratio="1" class="rounded-lg elevation-1" contain />
              </v-col>
            </v-row>
            <v-btn class="create-btn" :loading="submitting" :disabled="!valid" @click="submitCreate">
              <v-icon left small>mdi-send</v-icon>
              ส่ง Ticket
            </v-btn>
          </v-form>
        </template>

        <!-- ── DETAIL VIEW ── -->
        <template v-else-if="view === 'detail'">
          <div v-if="detailLoading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate size="32" width="3" />
          </div>
          <template v-else-if="ticket">
            <div class="mb-4 d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center">
              <div>
                <h4 class="title-text">{{ ticket.title }}</h4>
                <div class="text-caption grey--text mt-1">{{ formatDate(ticket.createdAt) }}</div>
                <v-chip
                  small
                  :color="ticket.status === 'open' ? '#fee2e2' : '#F3F4F6'"
                  :text-color="ticket.status === 'open' ? '#d6230b' : '#6b7280'"
                  class="mt-2 status-chip"
                >
                  {{ ticket.status === 'open' ? 'เปิดอยู่' : 'ปิดแล้ว' }}
                </v-chip>
              </div>
              <div class="mt-3 mt-sm-0" v-if="ticket.status === 'open'">
                <v-btn small color="error" outlined @click="confirmCloseDialog = true">
                  <v-icon left small>mdi-close-circle-outline</v-icon>
                  ปิด Ticket
                </v-btn>
              </div>
            </div>

            <v-dialog v-model="confirmCloseDialog" max-width="420">
              <v-card>
                <v-card-title class="headline">ยืนยันการปิด Ticket</v-card-title>
                <v-card-text>
                  ปิดแล้วจะไม่สามารถตอบกลับข้อความใน Ticket นี้ได้อีก ต้องการปิด Ticket นี้หรือไม่?
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="confirmCloseDialog = false">ยกเลิก</v-btn>
                  <v-btn color="error" :loading="closing" @click="handleConfirmClose">ยืนยันปิด</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <p class="text-body-2 mb-2">{{ ticket.description }}</p>
            <div v-if="ticket.ticketFiles && ticket.ticketFiles.length" class="mt-3">
              <div class="text-subtitle-2 mb-2">รูปภาพที่แนบกับ Ticket</div>
              <v-row dense>
                <v-col v-for="file in ticket.ticketFiles" :key="file.id" cols="6" sm="4" md="3">
                  <v-img :src="getImageUrl(file.fileUrl)" :alt="file.fileName" aspect-ratio="1" class="rounded-lg elevation-1" contain />
                </v-col>
              </v-row>
            </div>

            <v-divider class="my-4" />
            <div class="text-subtitle-2 font-weight-bold mb-3">ข้อความ</div>
            <div
              v-for="msg in (ticket.ticketMessages || [])"
              :key="msg.id"
              class="message-block mb-3"
              :class="{ 'message-block--admin': msg.adminId }"
            >
              <div class="text-caption grey--text mb-1">
                <v-icon x-small class="mr-1">{{ msg.userId ? 'mdi-account' : 'mdi-shield-account' }}</v-icon>
                {{ msg.userId ? "คุณ" : "แอดมิน" }} · {{ formatDateTime(msg.createdAt) }}
              </div>
              <div class="text-body-2 mb-1">{{ msg.message }}</div>
              <div v-if="msg.ticketMessageFiles && msg.ticketMessageFiles.length" class="mt-1">
                <v-row dense>
                  <v-col v-for="file in msg.ticketMessageFiles" :key="file.id" cols="6" sm="4" md="3">
                    <v-img :src="getImageUrl(file.fileUrl)" :alt="file.fileName" aspect-ratio="1" class="rounded-lg elevation-1" contain />
                  </v-col>
                </v-row>
              </div>
            </div>

            <v-divider class="my-4" />
            <div class="text-subtitle-2 font-weight-bold mb-3">ตอบกลับ</div>
            <template v-if="ticket.status === 'open'">
              <v-textarea v-model.trim="replyText" outlined dense rows="3" placeholder="พิมพ์ข้อความ..." class="mb-2" />
              <div class="mb-2 text-caption grey--text">แนบรูปภาพตอบกลับ (ถ้ามี)</div>
              <v-file-input
                multiple accept="image/*" show-size truncate-length="25"
                prepend-icon="mdi-image-multiple" label="เลือกรูปภาพ" outlined dense
                @change="onReplyFilesSelected"
              />
              <v-row v-if="replyPreviews.length" class="mt-3" dense>
                <v-col v-for="(src, idx) in replyPreviews" :key="idx" cols="6" sm="4" md="3">
                  <v-img :src="src" aspect-ratio="1" class="rounded-lg elevation-1" contain />
                </v-col>
              </v-row>
              <v-btn class="mt-3 create-btn" :loading="sending" :disabled="!replyText.trim()" @click="sendReply">
                <v-icon left small>mdi-send</v-icon>
                ส่งข้อความ
              </v-btn>
            </template>
            <template v-else>
              <div class="text-body-2 grey--text">Ticket นี้ถูกปิดแล้ว ไม่สามารถตอบกลับเพิ่มเติมได้</div>
            </template>
          </template>
          <p v-else class="grey--text">ไม่พบ Ticket</p>
        </template>

      </div><!-- /section-form -->
    </v-card>
  </div>
</template>

<script>
import userApi from "@/services/api/api";
import { listTicketsApi, createTicketApi, getTicketByIdApi, addTicketMessageApi, uploadTicketFilesApi, closeTicketApi } from "@/services/api/ticket";
import { getImageUrl } from "@/utils/image";

export default {
  name: "ProfileUserTickets",
  props: {
    label: { type: String, default: "แจ้งปัญหา / Ticket" },
  },
  data() {
    return {
      view: "list",
      loading: false,
      detailLoading: false,
      tickets: [],
      filterStatus: "",
      statusItems: [
        { text: "ทั้งหมด", value: "" },
        { text: "เปิด", value: "open" },
        { text: "ปิด", value: "closed" },
      ],
      valid: false,
      submitting: false,
      form: { title: "", description: "", message: "" },
      selectedId: null,
      ticket: null,
      replyText: "",
      sending: false,
      closing: false,
      confirmCloseDialog: false,
      createFiles: [],
      createPreviews: [],
      replyFiles: [],
      replyPreviews: [],
    };
  },
  watch: {
    view(val) {
      if (val === "list") this.fetchTickets();
      if (val === "detail" && this.selectedId) this.fetchTicket();
    },
  },
  created() {
    this.fetchTickets();
  },
  methods: {
    truncate(val, len = 60) {
      if (!val) return "";
      return val.length > len ? val.slice(0, len) + "…" : val;
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleDateString("th-TH");
    },
    formatDateTime(d) {
      if (!d) return "";
      return new Date(d).toLocaleString("th-TH");
    },
    getImageUrl,
    onCreateFilesSelected(files) {
      const normalized = Array.isArray(files) ? files : files ? [files] : [];
      this.createFiles = normalized;
      this.createPreviews = normalized.map((f) => (f ? URL.createObjectURL(f) : null));
    },
    onReplyFilesSelected(files) {
      const normalized = Array.isArray(files) ? files : files ? [files] : [];
      this.replyFiles = normalized;
      this.replyPreviews = normalized.map((f) => (f ? URL.createObjectURL(f) : null));
    },
    async uploadFilesIfAny(files) {
      if (!files || !files.length) return [];
      const formData = new FormData();
      files.forEach((file) => {
        if (file) formData.append("files", file);
      });
      const res = await uploadTicketFilesApi(userApi, formData);
      return res.data || [];
    },
    async fetchTickets() {
      this.loading = true;
      try {
        const res = await listTicketsApi(userApi, this.filterStatus ? { status: this.filterStatus } : {});
        this.tickets = res.data || [];
      } catch (e) {
        if (this.$root.$emit) this.$root.$emit("snack", e.response?.data?.message || "โหลดรายการ Ticket ไม่สำเร็จ");
      } finally {
        this.loading = false;
      }
    },
    openDetail(id) {
      this.selectedId = id;
      this.ticket = null;
      this.view = "detail";
    },
    backToList() {
      this.view = "list";
      this.selectedId = null;
      this.ticket = null;
      this.replyText = "";
    },
    handleBack() {
      if (this.view === "create" || this.view === "detail") {
        this.backToList();
      }
    },
    async fetchTicket() {
      if (!this.selectedId) return;
      this.detailLoading = true;
      try {
        const res = await getTicketByIdApi(userApi, this.selectedId);
        this.ticket = res.data;
      } catch (e) {
        this.ticket = null;
      } finally {
        this.detailLoading = false;
      }
    },
    async submitCreate() {
      if (!this.$refs.form.validate()) return;
      this.submitting = true;
      try {
        const files = await this.uploadFilesIfAny(this.createFiles);
        await createTicketApi(userApi, {
          title: this.form.title,
          description: this.form.description,
          message: this.form.message || undefined,
          files,
        });
        this.form = { title: "", description: "", message: "" };
        this.createFiles = [];
        this.createPreviews = [];
        this.view = "list";
        this.fetchTickets();
        if (this.$root.$emit) this.$root.$emit("snack", "สร้าง Ticket สำเร็จ");
      } catch (e) {
        if (this.$root.$emit) this.$root.$emit("snack", e.response?.data?.message || "สร้าง Ticket ไม่สำเร็จ");
      } finally {
        this.submitting = false;
      }
    },
    async sendReply() {
      if (!this.replyText.trim() || !this.ticket || this.ticket.status !== "open") return;
      this.sending = true;
      try {
        const files = await this.uploadFilesIfAny(this.replyFiles);
        await addTicketMessageApi(userApi, this.ticket.id, {
          message: this.replyText.trim(),
          files,
        });
        this.replyText = "";
        this.replyFiles = [];
        this.replyPreviews = [];
        await this.fetchTicket();
        if (this.$root.$emit) this.$root.$emit("snack", "ส่งข้อความแล้ว");
      } catch (e) {
        if (this.$root.$emit) this.$root.$emit("snack", e.response?.data?.message || "ส่งข้อความไม่สำเร็จ");
      } finally {
        this.sending = false;
      }
    },
    async closeTicket() {
      if (!this.ticket) return;
      this.closing = true;
      try {
        await closeTicketApi(userApi, this.ticket.id);
        await this.fetchTicket();
        if (this.$root.$emit) this.$root.$emit("snack", "ปิด Ticket แล้ว");
      } catch (e) {
        if (this.$root.$emit) this.$root.$emit("snack", e.response?.data?.message || "ปิด Ticket ไม่สำเร็จ");
      } finally {
        this.closing = false;
      }
    },
    async handleConfirmClose() {
      this.confirmCloseDialog = false;
      await this.closeTicket();
    },
  },
};
</script>

<style scoped>
/* ── Card Layout (เหมือน OrderHistory) ── */
.section-card {
  background: #fff;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(2, 6, 23, 0.06);
}

.card-head {
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  overflow: hidden;
}

.head-gradient {
  height: 76px;
  opacity: 0.18;
}

.head-gradient--red {
  background: linear-gradient(100deg, #d6230b 0%, #eed7d5 100%);
}

.head-inner {
  padding: 22px 24px 0;
}

.form-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 4px;
  font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
}

.subtitle {
  color: #6b7280;
  font-size: 13px;
}

.section-form {
  padding: 22px 24px 28px;
}

.create-btn {
  background: #d6230b !important;
  color: #fff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
}

/* ── Ticket Row ── */
.ticket-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.ticket-row {
  padding: 16px 4px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  border-radius: 10px;
  transition: background 0.15s;
}

.ticket-row:hover {
  background: #f8fafc;
}

.ticket-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff1f2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ticket-title {
  font-size: 15px;
  color: #0f172a;
  line-height: 1.4;
}

.ticket-sub {
  font-size: 13px;
  line-height: 1.4;
}

.status-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
  font-size: 11px !important;
}

/* ── Detail / Message ── */
.title-text {
  font-size: 16px;
  font-weight: 600;
  color: #1f2a39;
}

.message-block {
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 10px;
  border-left: 3px solid #e2e8f0;
}

.message-block--admin {
  background: #fff1f2;
  border-left-color: #d6230b;
}

/* ── Empty ── */
.empty-wrap {
  text-align: center;
  padding: 60px 0;
}

.empty-title {
  font-size: 18px;
  font-weight: 700;
  color: #212121;
  margin-top: 8px;
}

.empty-sub {
  color: #757575;
  font-size: 14px;
  margin-top: 4px;
}
</style>
